/**
 * 画笔采样管线（M2-09 / DESIGN.md §5.2）：
 *
 *   输入点流 → 指数平滑 → Catmull-Rom 样条 → 按 spacing 采样 stamp 位置
 *
 * 全程纯算术、无时钟无随机——同一输入点流**逐位**产出同一 stamp 序列，
 * 这是重做（历史重放笔画）与测试的共同前提。
 *
 * 压感：有压感设备直接用 pressure；没有的（鼠标）按速度回退——
 * 快笔画轻、慢笔画重，模拟真实运笔。两条路径的曲线都在这里，
 * 工具层只管把 PointerEvent 翻译成 {@link InputPoint}。
 */

export interface InputPoint {
  /** 文档空间像素。 */
  readonly x: number;
  readonly y: number;
  /** 压感 0–1；无压感设备（鼠标）为 null，走速度回退。 */
  readonly pressure: number | null;
  /** 事件时间戳（ms）。只用于速度回退曲线，不影响有压感的路径。 */
  readonly timeMs: number;
}

/** 一个 stamp 的落点与强度（尺寸/流量的压感缩放已算好）。 */
export interface Stamp {
  readonly x: number;
  readonly y: number;
  /** 直径，文档像素。 */
  readonly size: number;
  /** 本 stamp 的流量 0–1。 */
  readonly flow: number;
}

export interface SamplerSettings {
  /** 基础直径（文档像素）。 */
  readonly size: number;
  /** 基础流量 0–1。 */
  readonly flow: number;
  /** stamp 间距，直径的倍数（Photoshop 默认 0.25）。 */
  readonly spacing: number;
  /** 平滑强度 0–1；0 = 不平滑。指数平滑的 α = 1 − smoothing。 */
  readonly smoothing: number;
  /** 压感是否驱动大小 / 流量。 */
  readonly pressureSize: boolean;
  readonly pressureFlow: boolean;
}

export const DEFAULT_SAMPLER: SamplerSettings = {
  size: 30,
  flow: 1,
  spacing: 0.25,
  smoothing: 0.5,
  pressureSize: true,
  pressureFlow: true,
};

/**
 * 速度 → 等效压感的回退曲线（无压感设备）。
 *
 * v 单位是 文档像素/ms。5px/ms（相当明快的一笔）→ 0.3；静止 → 1。
 * 下限 0.3 而不是 0：快速勾线也要留下可见的痕迹。
 */
export function pressureFromSpeed(v: number): number {
  const raw = 1 - v / 5;
  return Math.min(1, Math.max(0.3, raw));
}

interface Smoothed {
  readonly x: number;
  readonly y: number;
  readonly pressure: number;
}

/** Catmull-Rom 在 t∈[0,1] 上对 p1→p2 段插值（p0/p3 是控制邻点）。 */
function catmullRom(p0: number, p1: number, p2: number, p3: number, t: number): number {
  const t2 = t * t;
  const t3 = t2 * t;
  return (
    0.5 *
    (2 * p1 +
      (-p0 + p2) * t +
      (2 * p0 - 5 * p1 + 4 * p2 - p3) * t2 +
      (-p0 + 3 * p1 - 3 * p2 + p3) * t3)
  );
}

/** 每段样条的定长细分数。采样密度的上界由 spacing 决定，16 足够平滑。 */
const SUBDIVISIONS = 16;

export class StrokeSampler {
  readonly #settings: SamplerSettings;
  /** 平滑后的控制点窗口，最多 4 个（Catmull-Rom 需要）。 */
  readonly #window: Smoothed[] = [];
  #smoothState: Smoothed | null = null;
  #lastInput: InputPoint | null = null;
  /** 距上一个 stamp 的剩余弧长。 */
  #residual = 0;
  #emittedAny = false;

  constructor(settings: SamplerSettings = DEFAULT_SAMPLER) {
    if (settings.size <= 0) throw new RangeError('画笔直径必须为正。');
    if (settings.spacing <= 0) throw new RangeError('spacing 必须为正，0 会产生无限 stamp。');
    this.#settings = settings;
  }

  /** 喂入一个输入点，返回由此产生的 stamp（可能为空）。 */
  add(point: InputPoint): Stamp[] {
    const pressure = this.#effectivePressure(point);
    this.#lastInput = point;

    const alpha = 1 - this.#settings.smoothing;
    const previous = this.#smoothState;
    const smoothed: Smoothed =
      previous === null
        ? { x: point.x, y: point.y, pressure }
        : {
            x: previous.x + alpha * (point.x - previous.x),
            y: previous.y + alpha * (point.y - previous.y),
            pressure: previous.pressure + alpha * (pressure - previous.pressure),
          };
    this.#smoothState = smoothed;

    this.#window.push(smoothed);
    if (this.#window.length === 1) {
      // 起笔：立刻落一个 stamp——点一下也该留痕
      this.#emittedAny = true;
      return [this.#stampAt(smoothed)];
    }
    if (this.#window.length < 4) {
      // 样条窗口未满，先用复制端点凑段（等价于起始段直线）
      return this.#emitSegment();
    }
    if (this.#window.length > 4) this.#window.shift();
    return this.#emitSegment();
  }

  /**
   * 收笔：先「追帽」再冲尾段。
   *
   * 指数平滑让笔迹**滞后**于光标（这是平滑的本质），松手时必须把
   * 原始终点作为未平滑点补进样条——否则笔画永远短一截，
   * 平滑越强短得越多（Photoshop 的 catch-up 行为）。
   */
  finish(): Stamp[] {
    if (this.#window.length < 1) return [];
    const stamps: Stamp[] = [];

    const raw = this.#lastInput;
    const smoothed = this.#smoothState;
    if (raw !== null && smoothed !== null && Math.hypot(raw.x - smoothed.x, raw.y - smoothed.y) > 0.5) {
      this.#window.push({ x: raw.x, y: raw.y, pressure: smoothed.pressure });
      if (this.#window.length > 4) this.#window.shift();
      if (this.#window.length >= 2) stamps.push(...this.#emitSegment());
    }

    if (this.#window.length < 2) return stamps;
    // 复制末点作为幽灵邻点，把最后一段也画出来
    const last = this.#window.at(-1)!;
    this.#window.push(last);
    if (this.#window.length > 4) this.#window.shift();
    stamps.push(...this.#emitSegment());
    return stamps;
  }

  #effectivePressure(point: InputPoint): number {
    if (point.pressure !== null) return Math.min(1, Math.max(0, point.pressure));
    const previous = this.#lastInput;
    if (previous === null) return pressureFromSpeed(0);
    const dt = Math.max(1, point.timeMs - previous.timeMs);
    const dist = Math.hypot(point.x - previous.x, point.y - previous.y);
    return pressureFromSpeed(dist / dt);
  }

  #stampAt(p: Smoothed): Stamp {
    const { size, flow, pressureSize, pressureFlow } = this.#settings;
    return {
      x: p.x,
      y: p.y,
      size: pressureSize ? Math.max(1, size * p.pressure) : size,
      flow: pressureFlow ? flow * p.pressure : flow,
    };
  }

  /** 对窗口的当前「中段」按弧长与 spacing 采样。 */
  #emitSegment(): Stamp[] {
    const w = this.#window;
    // 窗口不满 4 时用端点复制
    const p0 = w.length >= 4 ? w[w.length - 4]! : w[0]!;
    const p1 = w.length >= 3 ? w[w.length - 3]! : w[0]!;
    const p2 = w[w.length - 2]!;
    const p3 = w[w.length - 1]!;

    const stamps: Stamp[] = [];
    let prev = { x: catmullRom(p0.x, p1.x, p2.x, p3.x, 0), y: catmullRom(p0.y, p1.y, p2.y, p3.y, 0) };
    for (let i = 1; i <= SUBDIVISIONS; i++) {
      const t = i / SUBDIVISIONS;
      const cur = {
        x: catmullRom(p0.x, p1.x, p2.x, p3.x, t),
        y: catmullRom(p0.y, p1.y, p2.y, p3.y, t),
      };
      const pressure = p1.pressure + (p2.pressure - p1.pressure) * t;
      let stepLength = Math.hypot(cur.x - prev.x, cur.y - prev.y);
      // 沿细分线段按 spacing 落 stamp；spacing 随压感尺寸变化
      while (stepLength > 0) {
        const stampSize = this.#settings.pressureSize
          ? Math.max(1, this.#settings.size * pressure)
          : this.#settings.size;
        const spacingPx = Math.max(0.5, stampSize * this.#settings.spacing);
        const need = spacingPx - this.#residual;
        if (stepLength < need) {
          this.#residual += stepLength;
          break;
        }
        const ratio = need / stepLength;
        prev = { x: prev.x + (cur.x - prev.x) * ratio, y: prev.y + (cur.y - prev.y) * ratio };
        stepLength -= need;
        this.#residual = 0;
        this.#emittedAny = true;
        stamps.push(this.#stampAt({ x: prev.x, y: prev.y, pressure }));
      }
      prev = cur;
    }
    return stamps;
  }

  get emittedAny(): boolean {
    return this.#emittedAny;
  }
}
